import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PublicScan from "@/pages/PublicScan";
import ClaimTag from "@/pages/ClaimTag";
import UserShell from "@/pages/UserShell";
import UserDashboard from "@/pages/UserDashboard";
import TagManage from "@/pages/TagManage";
import Activity from "@/pages/Activity";
import Profile from "@/pages/Profile";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTags from "@/pages/admin/AdminTags";
import AdminGenerate from "@/pages/admin/AdminGenerate";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminThemes from "@/pages/admin/AdminThemes";
import AdminScans from "@/pages/admin/AdminScans";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function HomeRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (user) return <Navigate to="/app" replace />;
  return <Landing />;
}

export default function App() {
  useEffect(() => { document.title = "Scan2Return — QR Lost & Found"; }, []);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/t/:token" element={<PublicScan />} />
        <Route path="/claim" element={<Protected><ClaimTag /></Protected>} />

        <Route element={<Protected><UserShell /></Protected>}>
          <Route path="/app" element={<UserDashboard />} />
          <Route path="/app/tags/:id" element={<TagManage />} />
          <Route path="/app/activity" element={<Activity />} />
          <Route path="/app/profile" element={<Profile />} />
        </Route>

        <Route path="/admin" element={<Protected adminOnly><AdminLayout /></Protected>}>
          <Route index element={<AdminDashboard />} />
          <Route path="tags" element={<AdminTags />} />
          <Route path="generate" element={<AdminGenerate />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="themes" element={<AdminThemes />} />
          <Route path="scans" element={<AdminScans />} />
          <Route path="messages" element={<AdminMessages />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
